import type { Editor, JSONContent, Range } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { ActionOrigin } from '../../../actions/actionTypes';
import { TiptapNodeType, type TiptapContent } from '../../tiptapTypes';
import { removeTaskItem } from './taskListItemDeletionUtils';
import { duplicateTaskItem } from './taskListItemDuplicationUtils';

const TASK_LIST_DROP_SYNC_KEY = 'task-list-drop';

/**
 * Applies a drop's document changes with every affected editor's synchroniser paused, so none of
 * them saves an intermediate state, then flushes each one with the caller's app transaction ID.
 *
 * Nothing is flushed when `applyDrop` reports the drop didn't apply, so a rejected drop leaves no
 * pending save behind.
 */
const applyTaskListDrop = ({
    editors,
    transactionId,
    actionOrigin,
    applyDrop,
}: {
    editors: Editor[];
    transactionId: number;
    actionOrigin?: ActionOrigin;
    applyDrop: () => boolean;
}): boolean => {
    editors.forEach((editor) => editor.storage.externalStoreSynchroniser.pauseUpdates(TASK_LIST_DROP_SYNC_KEY));

    let didApply = false;
    try {
        didApply = applyDrop();
    } finally {
        editors.forEach((editor) => editor.storage.externalStoreSynchroniser.resumeUpdates(TASK_LIST_DROP_SYNC_KEY));
    }

    if (!didApply) return false;

    editors.forEach((editor) => editor.commands.flushPendingUpdate({ transactionId, actionOrigin }));

    return true;
};

/**
 * Inserts a collection of task item JSON subtrees at an existing task-list sibling position.
 */
const insertTaskItemsAtPosition = ({
    editor,
    taskItems,
    position,
}: {
    editor: Editor;
    taskItems: JSONContent[];
    position: number;
}): boolean => {
    if (!taskItems.length || editor.state.doc.resolve(position).parent.type.name !== TiptapNodeType.taskList) {
        return false;
    }

    const nodes = taskItems.map((taskItem) => editor.schema.nodeFromJSON(taskItem));
    editor.view.dispatch(editor.state.tr.insert(position, Fragment.fromArray(nodes)));
    return true;
};

/**
 * Gets the top-level task items from a persisted single-editor task-list document.
 */
const getTopLevelTaskItems = (content: TiptapContent): JSONContent[] =>
    content.content.find((node) => node.type === TiptapNodeType.taskList)?.content ?? [];

/**
 * Gets the remaining task items in a nested list, plus the range that removes their now-empty wrapper when needed.
 */
const getFollowingTaskItems = ({
    editor,
    position,
}: {
    editor: Editor;
    position: number;
}): { taskItems: JSONContent[]; from: number; to: number } | null => {
    const $position = editor.state.doc.resolve(position);

    if ($position.parent.type.name !== TiptapNodeType.taskList) return null;

    const firstTaskIndex = $position.index();

    if (firstTaskIndex >= $position.parent.childCount) return null;

    const firstTask = $position.parent.child(firstTaskIndex);

    if (firstTask.type.name !== TiptapNodeType.taskItem) return null;

    const followingTaskItemNodes = $position.parent.content.content.slice(firstTaskIndex);
    const taskItems = followingTaskItemNodes.map((taskItem) => taskItem.toJSON());
    const removesEntireTaskList = firstTaskIndex === 0;
    const to = position + followingTaskItemNodes.reduce((size, taskItem) => size + taskItem.nodeSize, 0);

    return {
        taskItems,
        from: removesEntireTaskList ? $position.before() : position,
        to: removesEntireTaskList ? $position.after() : to,
    };
};

/**
 * Adds reparented task items to the end of a dragged task item's existing nested list.
 */
const appendTaskItemsToTaskItem = ({
    taskItem,
    taskItems,
}: {
    taskItem: JSONContent;
    taskItems: JSONContent[];
}): JSONContent => {
    const nestedTaskList = taskItem.content?.find((child) => child.type === TiptapNodeType.taskList);

    return {
        ...taskItem,
        content: [
            ...(taskItem.content?.filter((child) => child.type !== TiptapNodeType.taskList) ?? []),
            {
                ...(nestedTaskList ?? { type: TiptapNodeType.taskList }),
                content: [...(nestedTaskList?.content ?? []), ...taskItems],
            },
        ],
    };
};

/**
 * Builds (but does not dispatch) a transaction that inserts a task item at `position`, first
 * removing the following nested block and reparenting its items under the inserted task when a
 * reparenting position is given. Returns null when the insertion isn't valid, so callers can bail
 * before dispatching anything.
 */
const buildTaskItemInsertion = ({
    editor,
    taskItem,
    position,
    reparentFollowingTaskItemsFromPosition,
}: {
    editor: Editor;
    taskItem: JSONContent;
    position: number;
    reparentFollowingTaskItemsFromPosition?: number;
}): Transaction | null => {
    // The drop point must land inside a task list for the moved item to be a valid sibling there.
    if (editor.state.doc.resolve(position).parent.type.name !== TiptapNodeType.taskList) return null;

    const followingTaskItems =
        reparentFollowingTaskItemsFromPosition === undefined
            ? null
            : getFollowingTaskItems({ editor, position: reparentFollowingTaskItemsFromPosition });

    // Reparenting was requested but the following block couldn't be resolved (e.g. the position no
    // longer points at a task list), so abort rather than drop the item without its sub-tasks.
    if (reparentFollowingTaskItemsFromPosition !== undefined && !followingTaskItems) return null;

    const insertedTaskItem = followingTaskItems
        ? appendTaskItemsToTaskItem({ taskItem, taskItems: followingTaskItems.taskItems })
        : taskItem;
    const transaction = editor.state.tr;

    if (followingTaskItems) transaction.delete(followingTaskItems.from, followingTaskItems.to);

    transaction.insert(transaction.mapping.map(position), editor.schema.nodeFromJSON(insertedTaskItem));

    return transaction;
};

/**
 * Inserts a task item into the target editor, optionally reparenting all remaining items from a
 * following nested list. Used for cross-editor moves, where the source is removed separately.
 */
const insertTaskItemAtPosition = ({
    editor,
    taskItem,
    position,
    reparentFollowingTaskItemsFromPosition,
}: {
    editor: Editor;
    taskItem: JSONContent;
    position: number;
    reparentFollowingTaskItemsFromPosition?: number;
}): boolean => {
    const transaction = buildTaskItemInsertion({ editor, taskItem, position, reparentFollowingTaskItemsFromPosition });

    if (!transaction) return false;

    editor.view.dispatch(transaction);

    return true;
};

/**
 * Moves a task subtree into a different editor: insert into the target, then remove the source.
 * The source was validated on the untouched source document, so both dispatches always apply.
 */
const moveTaskItemAcrossEditors = ({
    sourceEditor,
    sourcePosition,
    targetEditor,
    taskItem,
    targetPosition,
    reparentFollowingTaskItemsFromPosition,
}: {
    sourceEditor: Editor;
    sourcePosition: number;
    targetEditor: Editor;
    taskItem: JSONContent;
    targetPosition: number;
    reparentFollowingTaskItemsFromPosition?: number;
}): boolean => {
    const didInsert = insertTaskItemAtPosition({
        editor: targetEditor,
        taskItem,
        position: targetPosition,
        reparentFollowingTaskItemsFromPosition,
    });

    if (!didInsert) return false;

    const sourceTransaction = sourceEditor.state.tr;

    removeTaskItem(sourceTransaction, sourcePosition);
    sourceEditor.view.dispatch(sourceTransaction);

    return true;
};

/**
 * Reorders a task within one document, optionally reparenting the following nested block.
 *
 * The whole move — deleting any reparented block, inserting the moved item, then removing the
 * original — is built as a single transaction and only dispatched once the source removal is
 * confirmed, so a failed re-derivation can never leave the document half-mutated.
 */
const moveTaskItemWithinEditor = ({
    editor,
    taskItem,
    sourceRange,
    targetPosition,
    reparentFollowingTaskItemsFromPosition,
}: {
    editor: Editor;
    taskItem: JSONContent;
    sourceRange: Range;
    targetPosition: number;
    reparentFollowingTaskItemsFromPosition?: number;
}): boolean => {
    // The gaps immediately before and after the source task are its current location, so dropping
    // there is a no-op. A reparenting drop is exempt: it legitimately re-parents the following block
    // even when the drop point sits within the source's range.
    const isDroppedAtSourceLocation = targetPosition >= sourceRange.from && targetPosition <= sourceRange.to;

    if (reparentFollowingTaskItemsFromPosition === undefined && isDroppedAtSourceLocation) return false;

    const transaction = buildTaskItemInsertion({
        editor,
        taskItem,
        position: targetPosition,
        reparentFollowingTaskItemsFromPosition,
    });

    if (!transaction) return false;

    // Reparenting the source's siblings can change whether it is the only child of its list, so
    // the original is removed from the in-progress document rather than the one it was resolved in.
    if (!removeTaskItem(transaction, transaction.mapping.map(sourceRange.from))) return false;

    editor.view.dispatch(transaction);
    return true;
};

/**
 * Moves a task subtree between task-list editors and persists both changes in one app transaction.
 */
export const moveTaskItemToTaskList = ({
    sourceEditor,
    sourcePosition,
    targetEditor,
    targetPosition,
    transactionId,
    reparentFollowingTaskItemsFromPosition,
}: {
    sourceEditor: Editor;
    sourcePosition: number;
    targetEditor: Editor;
    targetPosition: number;
    transactionId: number;
    // A ProseMirror position in the target document, at the task sitting in the drop gap. When set,
    // that task and all of its following siblings are removed from the target and re-parented as
    // sub-tasks of the dropped item.
    reparentFollowingTaskItemsFromPosition?: number;
}): boolean => {
    const taskItem = sourceEditor.state.doc.nodeAt(sourcePosition);

    if (taskItem?.type.name !== TiptapNodeType.taskItem) return false;

    const taskItemJson = taskItem.toJSON();
    const sourceRange = { from: sourcePosition, to: sourcePosition + taskItem.nodeSize };
    const isSameEditorMove = sourceEditor === targetEditor;

    // Both documents form one logical move, so they are paused and flushed together. De-duped so a
    // same-editor move doesn't pause/resume the synchroniser twice.
    return applyTaskListDrop({
        editors: [...new Set([sourceEditor, targetEditor])],
        transactionId,
        actionOrigin: ActionOrigin.taskItemMove,
        applyDrop: () =>
            isSameEditorMove
                ? moveTaskItemWithinEditor({
                      editor: sourceEditor,
                      taskItem: taskItemJson,
                      sourceRange,
                      targetPosition,
                      reparentFollowingTaskItemsFromPosition,
                  })
                : moveTaskItemAcrossEditors({
                      sourceEditor,
                      sourcePosition,
                      targetEditor,
                      taskItem: taskItemJson,
                      targetPosition,
                      reparentFollowingTaskItemsFromPosition,
                  }),
    });
};

/**
 * Whether reparenting from a position would move content out of the given task item: either the item
 * itself sits inside the following block, or the block sits inside the item's own nested list. Only
 * meaningful for positions in the same document, since both ranges are resolved there.
 */
const doesReparentedBlockOverlapTaskItem = ({
    editor,
    reparentFollowingTaskItemsFromPosition,
    taskItemPosition,
}: {
    editor: Editor;
    reparentFollowingTaskItemsFromPosition: number;
    taskItemPosition: number;
}): boolean => {
    const followingTaskItems = getFollowingTaskItems({ editor, position: reparentFollowingTaskItemsFromPosition });
    const taskItem = editor.state.doc.nodeAt(taskItemPosition);

    if (!followingTaskItems || !taskItem) return false;

    // Nesting means containment can run either way, so the half-open ranges are compared for overlap
    // rather than checking only whether the item falls inside the block.
    return taskItemPosition < followingTaskItems.to && followingTaskItems.from < taskItemPosition + taskItem.nodeSize;
};

/**
 * Copies a task subtree into a task-list editor and persists only the target's update.
 *
 * Deliberately target-only: the source task item is never removed and no separate source flush
 * occurs, so a copy arrives without moving the original and a single undo removes it.
 */
export const copyTaskItemToTaskList = ({
    sourceEditor,
    sourcePosition,
    targetEditor,
    targetPosition,
    transactionId,
    reparentFollowingTaskItemsFromPosition,
}: {
    sourceEditor: Editor;
    sourcePosition: number;
    targetEditor: Editor;
    targetPosition: number;
    transactionId: number;
    // As per `moveTaskItemToTaskList`. Reparented items only move within the target document, so
    // they keep their own task IDs — just the copied subtree is given fresh ones.
    reparentFollowingTaskItemsFromPosition?: number;
}): boolean => {
    const taskItem = sourceEditor.state.doc.nodeAt(sourcePosition);

    if (taskItem?.type.name !== TiptapNodeType.taskItem) return false;

    const duplicatedTaskItem = duplicateTaskItem(taskItem.toJSON());

    // Reparenting removes the following block from the target, which within one document can overlap
    // the source task itself — relocating the original under its own copy, or stripping the source's
    // sub-tasks and appending them to the copy.
    // A copy must never move the source, so fall back to a plain insert at the drop position.
    // Cross-editor copies can't overlap the source, so their reparenting is left alone.
    const reparentsSourceTaskItem =
        sourceEditor === targetEditor &&
        reparentFollowingTaskItemsFromPosition !== undefined &&
        doesReparentedBlockOverlapTaskItem({
            editor: targetEditor,
            reparentFollowingTaskItemsFromPosition,
            taskItemPosition: sourcePosition,
        });

    // A copy is an ordinary content change, so it omits `ActionOrigin.taskItemMove`: that origin
    // suppresses destination activity and re-parents the source's inline comment threads onto the
    // target, neither of which is correct while the original still exists.
    return applyTaskListDrop({
        editors: [targetEditor],
        transactionId,
        applyDrop: () =>
            insertTaskItemAtPosition({
                editor: targetEditor,
                taskItem: duplicatedTaskItem,
                position: targetPosition,
                reparentFollowingTaskItemsFromPosition: reparentsSourceTaskItem
                    ? undefined
                    : reparentFollowingTaskItemsFromPosition,
            }),
    });
};

/**
 * Merges the top-level items from a task-list element into a target editor and persists the target update
 * with the caller's app transaction ID. The caller is responsible for removing the source element.
 */
export const moveTaskListItemsToTaskList = ({
    sourceContent,
    targetEditor,
    targetPosition,
    transactionId,
}: {
    sourceContent: TiptapContent;
    targetEditor: Editor;
    targetPosition: number;
    transactionId: number;
}): boolean => {
    const taskItems = getTopLevelTaskItems(sourceContent);

    return applyTaskListDrop({
        editors: [targetEditor],
        transactionId,
        actionOrigin: ActionOrigin.taskItemMove,
        applyDrop: () =>
            insertTaskItemsAtPosition({
                editor: targetEditor,
                taskItems,
                position: targetPosition,
            }),
    });
};

/**
 * Copies the top-level items from a task-list element into a target editor and persists only the
 * target's update.
 *
 * Deliberately target-only: the source element keeps its own items and is never trashed, so an
 * Option-drag duplicates the list's contents into the target and a single undo removes them.
 */
export const copyTaskListItemsToTaskList = ({
    sourceContent,
    targetEditor,
    targetPosition,
    transactionId,
}: {
    sourceContent: TiptapContent;
    targetEditor: Editor;
    targetPosition: number;
    transactionId: number;
}): boolean => {
    const taskItems = getTopLevelTaskItems(sourceContent).map(duplicateTaskItem);

    // A copy is an ordinary content change, so it omits `ActionOrigin.taskItemMove` for the same
    // reasons as `copyTaskItemToTaskList`.
    return applyTaskListDrop({
        editors: [targetEditor],
        transactionId,
        applyDrop: () =>
            insertTaskItemsAtPosition({
                editor: targetEditor,
                taskItems,
                position: targetPosition,
            }),
    });
};

/**
 * Removes a task item that was moved out of its list, and flushes its editor with the caller's app
 * transaction ID. The list's only task is replaced with an empty one, so the list survives the move.
 */
export const deleteTaskItemWithTransaction = ({
    editor,
    position,
    transactionId,
}: {
    editor: Editor;
    position: number;
    transactionId: number;
}): boolean =>
    applyTaskListDrop({
        editors: [editor],
        transactionId,
        actionOrigin: ActionOrigin.taskItemMove,
        applyDrop: () => {
            const transaction = editor.state.tr;

            if (!removeTaskItem(transaction, position)) return false;

            editor.view.dispatch(transaction);
            return true;
        },
    });
